/**
 * CarDetails Organism Component
 * Detailed view of a car with specifications and features
 */

'use client'

import {
	CarImage,
	PriceTag,
	RatingStars,
	StatusBadge,
} from '@/components/atoms'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { Car } from '@/types'
import {
	Briefcase,
	Calendar,
	Check,
	Fuel,
	Gauge,
	MapPin,
	Users,
} from 'lucide-react'
import React from 'react'

interface CarDetailsProps {
	car: Car
	onReserve?: (car: Car) => void
	className?: string
}

export function CarDetails({ car, onReserve, className }: CarDetailsProps) {
	const [selectedImageIndex, setSelectedImageIndex] = React.useState(0)
	const selectedImage = car.images[selectedImageIndex]

	const specs = [
		{
			icon: Users,
			label: 'Pasajeros',
			value: `${car.specifications.seats} personas`,
		},
		{
			icon: Briefcase,
			label: 'Equipaje',
			value: `${car.specifications.luggage} maletas`,
		},
		{
			icon: Gauge,
			label: 'Transmisión',
			value:
				car.specifications.transmission === 'automatic'
					? 'Automático'
					: 'Manual',
		},
		{ icon: Fuel, label: 'Combustible', value: car.specifications.fuelType },
		{ icon: Calendar, label: 'Año', value: car.year.toString() },
		{ icon: MapPin, label: 'Ubicación', value: car.location },
	]

	const features = Object.entries(car.features)
		.filter(([, enabled]) => enabled)
		.map(([key]) => {
			const featureLabels: Record<string, string> = {
				airConditioning: 'Aire Acondicionado',
				gps: 'GPS',
				bluetooth: 'Bluetooth',
				usbPort: 'Puerto USB',
				backupCamera: 'Cámara Trasera',
				cruiseControl: 'Control de Crucero',
				sunroof: 'Techo Solar',
				heatedSeats: 'Asientos Calefactados',
			}
			return featureLabels[key] || key
		})

	return (
		<div className={cn('space-y-6', className)}>
			{/* Header */}
			<div className='flex flex-col md:flex-row md:items-start md:justify-between gap-4'>
				<div>
					<h1 className='text-3xl font-bold text-[#09194D]'>
						{car.brand} {car.model}
					</h1>
					<p className='text-gray-600 mt-1'>{car.year}</p>
					<div className='mt-2'>
						<RatingStars
							rating={car.rating}
							reviewCount={car.reviewCount}
						/>
					</div>
				</div>

				<StatusBadge status={car.availability ? 'available' : 'unavailable'} />
			</div>

			{/* Image Gallery */}
			<Card>
				<CardContent className='p-0'>
					<div className='relative h-96 bg-gray-100'>
						<CarImage
							src={selectedImage?.url || ''}
							alt={selectedImage?.alt || `${car.brand} ${car.model}`}
							fill
							objectFit='contain'
						/>
					</div>

					{/* Thumbnails */}
					{car.images.length > 1 && (
						<div className='flex gap-2 p-4 overflow-x-auto'>
							{car.images.map((image, index) => (
								<button
									key={image.id}
									onClick={() => setSelectedImageIndex(index)}
									className={cn(
										'relative w-20 h-20 flex-shrink-0 rounded-md overflow-hidden border-2 transition-all cursor-pointer',
										selectedImageIndex === index
											? 'border-[#09194D] scale-105'
											: 'border-gray-200 hover:border-gray-300'
									)}
								>
									<CarImage
										src={image.url}
										alt={image.alt}
										fill
										objectFit='cover'
									/>
								</button>
							))}
						</div>
					)}
				</CardContent>
			</Card>

			<div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
				{/* Specifications and Features */}
				<div className='lg:col-span-2 space-y-6'>
					{/* Description */}
					{car.description && (
						<Card>
							<CardContent className='p-6'>
								<h2 className='text-xl font-semibold text-[#09194D] mb-3'>
									Descripción
								</h2>
								<p className='text-gray-700'>{car.description}</p>
							</CardContent>
						</Card>
					)}

					{/* Specifications */}
					<Card>
						<CardContent className='p-6'>
							<h2 className='text-xl font-semibold text-[#09194D] mb-4'>
								Especificaciones
							</h2>
							<div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
								{specs.map((spec, index) => (
									<div
										key={index}
										className='flex items-start gap-3'
									>
										<spec.icon className='w-5 h-5 text-[#09194D] mt-0.5' />
										<div>
											<p className='text-sm text-gray-600'>{spec.label}</p>
											<p className='font-medium text-[#09194D]'>{spec.value}</p>
										</div>
									</div>
								))}
							</div>
						</CardContent>
					</Card>

					{/* Features */}
					{features.length > 0 && (
						<Card>
							<CardContent className='p-6'>
								<h2 className='text-xl font-semibold text-[#09194D] mb-4'>
									Características
								</h2>
								<div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
									{features.map((feature, index) => (
										<div
											key={index}
											className='flex items-center gap-2'
										>
											<Check className='w-4 h-4 text-[#CDFF00]' />
											<span className='text-gray-700'>{feature}</span>
										</div>
									))}
								</div>
							</CardContent>
						</Card>
					)}
				</div>

				{/* Pricing Card */}
				<div className='lg:col-span-1'>
					<Card className='sticky top-6'>
						<CardContent className='p-6 space-y-4'>
							<div>
								<p className='text-sm text-gray-600 mb-2'>Precio por día</p>
								<PriceTag
									amount={car.pricing.dailyRate}
									currency={car.pricing.currency}
									period='day'
									size='lg'
								/>
							</div>

							{car.pricing.weeklyRate && (
								<div>
									<p className='text-sm text-gray-600 mb-1'>
										Precio por semana
									</p>
									<PriceTag
										amount={car.pricing.weeklyRate}
										currency={car.pricing.currency}
										period='week'
										size='md'
									/>
								</div>
							)}

							{car.pricing.monthlyRate && (
								<div>
									<p className='text-sm text-gray-600 mb-1'>Precio por mes</p>
									<PriceTag
										amount={car.pricing.monthlyRate}
										currency={car.pricing.currency}
										period='month'
										size='md'
									/>
								</div>
							)}

							<div className='pt-4 space-y-2 border-t'>
								<div className='flex justify-between text-sm'>
									<span className='text-gray-600'>Depósito</span>
									<span className='font-medium'>
										{new Intl.NumberFormat('es-MX', {
											style: 'currency',
											currency: car.pricing.currency,
										}).format(car.pricing.deposit)}
									</span>
								</div>
								<div className='flex justify-between text-sm'>
									<span className='text-gray-600'>Seguro</span>
									<span className='font-medium'>
										{new Intl.NumberFormat('es-MX', {
											style: 'currency',
											currency: car.pricing.currency,
										}).format(car.pricing.insurance)}
									</span>
								</div>
							</div>

							<Button
								onClick={() => onReserve?.(car)}
								disabled={!car.availability}
								className='w-full bg-[#09194D] hover:bg-[#09194D]/90 text-white cursor-pointer'
								size='lg'
							>
								{car.availability ? 'Reservar Ahora' : 'No Disponible'}
							</Button>
						</CardContent>
					</Card>
				</div>
			</div>
		</div>
	)
}
